/* eslint-disable max-depth */
/* eslint-disable react-func/max-lines-per-function */
import React, {
  createElement,
  CSSProperties,
  SVGProps,
  JSXElementConstructor,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  PropsWithChildren,
  useRef,
} from 'react';
import cls from 'classnames';
import { omit } from 'lodash-es';

interface IconSetItem {
  properties: {
    name: string;
  };
  icon: {
    paths: string[];
    attrs?: { [key: string]: any }[];
    width?: number | string;
  };
  [key: string]: any;
}

interface IconSet {
  icons: IconSetItem[];
}

interface EeveeIconContextProviderValue {
  /** CDN 链接 */
  cdnLink?: string;
  /** 设置图标默认大小 */
  defaultSize?: number;
  /** 图标数据集 */
  iconSet?: IconSet;
  /** 远程图标数据 */
  remoteIconData?: {
    icons: IconSetItem[];
  };
  /** 缓存的图标 */
  storeFetchedIcons: React.MutableRefObject<Map<string, IconSetItem>>;
}

type EeveeIconContextProviderProps = Omit<EeveeIconContextProviderValue, 'remoteIconData' | 'storeFetchedIcons'>;

export interface EeveeIconProps extends SVGProps<SVGElement> {
  /**
   * 图标名称
   * @required
   */
  name: string;
  /**
   * 图标数据集
   */
  iconSet?: IconSet;
  /**
   * 图标尺寸
   * @default 20
   */
  size?: string | number;
  /**
   * 图标 title
   */
  title?: string;
  /**
   * 禁用 svg 图标 fill
   * @default false
   */
  disableFill?: boolean;
  /**
   * 图标旋转角度
   */
  rotate?: number | string;
  /**
   * 是否移除内联样式
   * @default false
   */
  removeInlineStyle?: boolean;
  style?: CSSProperties;
  wrapperClassName?: string;
  SvgComponent?: JSXElementConstructor<any>;
  PathComponent?: JSXElementConstructor<any>;
}

const CombinedNameReg = /[+:+]/;
const FETCH_ICON_URL = 'https://eevee-icon.apps-hp.danlu.netease.com/api/getGroupIconByName';
const EeveeIconContext = createContext<EeveeIconContextProviderValue | null>(null);

const FetchData = async (url: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(res => res.json())
      .then(result => resolve(result))
      .catch(e => {
        reject(e.toString());
      });
  });
};

export const EeveeIconContextProvider = (props: PropsWithChildren<EeveeIconContextProviderProps>) => {
  const { cdnLink, defaultSize, iconSet, ...restProps } = props;
  const [remoteIconData, setRemoteIconData] = useState({ icons: [] });
  const storeFetchedIcons = useRef(new Map());

  const getRemoteData = useCallback(async () => {
    if (!cdnLink) return;
    try {
      const iconData = await FetchData(cdnLink);
      setRemoteIconData(iconData);
    } catch {}
  }, [cdnLink]);

  useEffect(() => {
    getRemoteData();
  }, [getRemoteData]);

  return (
    <EeveeIconContext.Provider
      value={{
        cdnLink,
        iconSet,
        storeFetchedIcons,
        remoteIconData,
        defaultSize,
      }}
      {...restProps}
    />
  );
};

export const EeveeIcon = ({
  iconSet,
  name,
  title,
  rotate,
  removeInlineStyle = false,
  disableFill = false,
  size,
  SvgComponent,
  PathComponent,
  wrapperClassName,
  ...restProps
}: EeveeIconProps) => {
  const {
    iconSet: contextIconSet,
    cdnLink,
    defaultSize,
    remoteIconData,
    storeFetchedIcons,
  } = useContext(EeveeIconContext) || {};
  const [currentRemoteIcon, setCurrentRemoteIcon] = useState<IconSetItem>(null as any);

  const isCombinedIconName = useMemo(() => CombinedNameReg.test(name), [name]);

  // 动态获取远程图标
  const getCurrentRemoteIcon = useCallback(async () => {
    if (!isCombinedIconName) return;

    // 判断是否已缓存当前将要请求的图标数据
    if (storeFetchedIcons) {
      const alreadyStoredIcon = storeFetchedIcons.current.get(name);
      if (alreadyStoredIcon) {
        return setCurrentRemoteIcon(alreadyStoredIcon);
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('[Eevee-Icon]:请在组件外包裹 ConfigIconProvider，否则将不会缓存当前请求的图标数据');
    }

    // 请求当前图标数据
    const [group, iconName] = name.split(':');
    try {
      const { code, data } = await FetchData(`${FETCH_ICON_URL}?group=${group}&iconName=${iconName}`);
      if (code === 200) {
        setCurrentRemoteIcon(data);
        if (storeFetchedIcons) {
          storeFetchedIcons.current.set(name, data);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCombinedIconName, name]);

  useEffect(() => {
    getCurrentRemoteIcon();
  }, [getCurrentRemoteIcon]);

  const initialStyle: CSSProperties = useMemo(
    () => ({
      display: 'inline-block',
      stroke: 'currentColor',
      fill: 'currentColor',
    }),
    []
  );

  const computedStyle = useMemo(
    () => ({
      ...(removeInlineStyle ? {} : initialStyle),
      ...{
        width: size || defaultSize || '1em',
        height: size || defaultSize || '1em',
      },
      ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}),
      ...(restProps.style || {}),
    }),
    [defaultSize, initialStyle, removeInlineStyle, restProps.style, rotate, size]
  );

  const currentIcon = useMemo(
    () =>
      isCombinedIconName
        ? currentRemoteIcon
        : (iconSet || contextIconSet || remoteIconData)?.icons?.find(item => item.properties.name === name),
    [isCombinedIconName, currentRemoteIcon, iconSet, contextIconSet, remoteIconData, name]
  );

  const viewBox = useMemo(() => {
    if (currentIcon) {
      const { width = '1024' } = currentIcon.icon;
      return `0 0 ${width} 1024`;
    } else {
      return null;
    }
  }, [currentIcon]);

  const children = useMemo(
    () =>
      !!currentIcon
        ? currentIcon.icon.paths.map((path, index) => {
            const attrs = currentIcon.icon.attrs?.[index];

            const pathProps = {
              d: path,
              key: name + index,
              ...(!disableFill && attrs ? omit(attrs, 'pId') : {}),
            };

            return createElement(PathComponent || 'path', pathProps);
          })
        : [],
    [PathComponent, currentIcon, disableFill, name]
  );

  if (!(name || iconSet || cdnLink)) return null;
  if (!currentIcon) return null;

  if (title) {
    children.push(createElement('title', { key: title }, title));
  }
  return (
    <span
      className={cls('eevee-icon', wrapperClassName)}
      style={{
        fontSize: `${size || defaultSize}px`,
      }}>
      {createElement(SvgComponent || 'svg', { ...restProps, viewBox, style: computedStyle }, children)}
    </span>
  );
};
